import React, { useState } from "react";
import SidebarAdmin from "./SidebarAdmin";
import AdminNavbar from "./AdminNavbar";
import useAdminOTC from "../Hooks/useAdminOTC";
import AdminSearchBar from "../Assets/AdminSearchBar";
import AdminOtcTable from "./AdminOtcTable";
import PostAdminButton from "../Assets/PostAdminButton";
import ProductModal from "./ProductModal";
import ModalConfirmation from "../Assets/ModalConfirmation";
import ProductEditModal from "./ProductEditModal";
import { Product } from "../Types/Types";

interface AdminOTCProps {
    product: string;
    name: string;
    category: string;
    lang: string

}


const AdminOTC: React.FC<AdminOTCProps> = ({ product, name, category, lang }) => {


    const [modalOpen, setModalOpen] = useState(false);
    const [itemsPerPage, setItemsPerPage] = useState<number>(5);
    const [showForm, setShowForm] = useState(false); // Toggle form visibility
    const [selectedProductId, setsSlectedProductId] = useState<string | null>(null);
    const [showEditForm, setShowEditForm] = useState(false);
    const [productToEdit, setProductToEdit] = useState<Product | null>(null);

    const {
        products,
        loading,
        error,
        currentPage,
        setCurrentPage,
        totalCount,
        searchQuery,
        setSearchQuery,
        sortBy,
        setSortBy,
        sortOrder,
        setSortOrder,
        createProduct,
        updateProduct,
        deleteProduct,
        reorder,
        success,
        setSuccess

    } = useAdminOTC(itemsPerPage, product);


    const handleSearch = (query: string) => {
        setSearchQuery(query); // Update the search query state
        setCurrentPage(1); // Optionally reset the page to 1 when search query changes
    };

    const handleRowsPerPageChange = (newRowsPerPage: number) => {
        setItemsPerPage(newRowsPerPage); // Dynamically update itemsPerPage state
        setCurrentPage(1); // Optionally reset the current page when rows per page change
    };


    const handleDeleteRequest = (id: string) => {
        setsSlectedProductId(id);
        setModalOpen(true);
    };

    // ✅ Confirm deletion
    const handleDeleteConfirm = () => {


        if (selectedProductId) {
            deleteProduct(selectedProductId);
            setModalOpen(false);
            setsSlectedProductId(null);
        }
    };

    const handleDeleteCancel = () => {
        setModalOpen(false);
        setsSlectedProductId(null);
    };

    // Display loading state

    return (
        <div className="min-h-screen bg-gray-100 flex">
            {/* Sidebar */}
            <div className="w-64">
                <SidebarAdmin />
            </div>

            <div className="flex-1 flex flex-col">
                {/* Navbar */}
                <AdminNavbar />

                {/* Main Content */}
                <div className="flex-1 py-12 px-48 space-y-12">
                    <h1 className="text-4xl text-blue-900">{name}</h1>
                    <div className="flex justify-between items-center space-x-4">
                        <PostAdminButton onClick={() => setShowForm(true)} />
                        <AdminSearchBar searchQuery={searchQuery} onSearch={handleSearch} />
                    </div>

                    

                    <AdminOtcTable
                        products={products}
                        loading={loading}
                        error={error}
                        sortBy={sortBy}
                        sortOrder={sortOrder}
                        currentPage={currentPage}
                        totalCount={totalCount}
                        onSortChange={(field, order) => {
                            setSortBy(field);
                            setSortOrder(order);
                        }}
                        onPageChange={setCurrentPage}
                        onRowsPerPageChange={handleRowsPerPageChange}
                        onDelete={handleDeleteRequest}
                        onEdit={(productData) => {
                            setProductToEdit(productData);
                            setShowEditForm(true)
                        }}
                        onReorder={async (newOrder: Product[]) => {
                            // Map the array to include the position index
                            const reorderedWithPosition = newOrder.map((item, index) => ({
                                _id: item._id,
                                position: index + 1,  // or just index if your backend expects 0-based
                            }));
                            await reorder(reorderedWithPosition);
                        }}
                        success={success}
                        setSuccess={setSuccess}
                    />
                </div>
            </div>

            <ProductModal
                isOpen={showForm}
                onClose={() => setShowForm(false)}
                category={category}
                name={name}
                onSubmit={async (productData) => {
                    await createProduct(productData);
                    setShowForm(false);
                    setCurrentPage(1);
                }}
            />

            {showEditForm && (
                <ProductEditModal
                    isOpen={showEditForm}
                    onClose={() => setShowEditForm(false)}
                    category={category}
                    categoryName={name}
                    onSubmit={async (productId, productData) => {
                        await updateProduct(productId, productData);
                        setShowEditForm(false);
                        setProductToEdit(null);
                        setCurrentPage(1);
                    }}
                    initialData={productToEdit!}
                    lang={localStorage.getItem('lang') || 'en'} // Or whatever state you're using for the language

                />
            )}

            <ModalConfirmation
                isOpen={modalOpen}
                onClose={handleDeleteCancel}
                onConfirm={handleDeleteConfirm}
            />
        </div>

    );
};

export default AdminOTC;
